// Logo colour checks on a Quote (CEO 2026-10-08).
//
// A website hard good is sold at the 1-colour price. When the customer's logo
// has more colours, the rep emails the client the extra-colour charge before
// invoicing, then records the answer here:
//   • "Client confirmed" — the suggested charges are added to the order;
//   • "Client declined (1 colour)" — printed in 1 colour, nothing added.
// The order can't be converted to an invoice while any check is open (the API
// refuses with logo_colours_unconfirmed). Renders nothing when there are none.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";

interface Check {
  id: string;
  lineItemId: string;
  colours: number | null;
  fullColour: boolean;
  message: string;
  suggested: Array<{ description: string; quantity: number; unitPrice: string }>;
  status: "pending" | "client_confirmed" | "client_declined";
}

const money = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function LogoColourChecks({ orderId, onResolved }: { orderId: string; onResolved?: () => void }) {
  const qc = useQueryClient();
  const key = ["merchd.orderLogoColourChecks", orderId];
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => api<{ checks: Check[] }>(`/orders/${orderId}/logo-colour-checks`),
  });
  const [error, setError] = useState<string | null>(null);
  const resolve = useMutation({
    mutationFn: (v: { checkId: string; decision: Check["status"] }) =>
      api(`/orders/${orderId}/logo-colour-checks/${v.checkId}`, { method: "POST", body: JSON.stringify({ decision: v.decision }) }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: key });
      onResolved?.();
    },
    onError: (e) => setError(e instanceof ApiError && (e.body as { error?: string } | null)?.error === "invoice_already_exists"
      ? "This order is already invoiced."
      : "Couldn't save that. Try again."),
  });

  const checks = data?.checks ?? [];
  if (checks.length === 0) return null;
  const open = checks.filter((c) => c.status === "pending");

  return (
    <div className="mb-4 rounded-lg border border-brown-100 bg-cream-50 px-4 py-3">
      <p className="text-sm font-semibold text-rust-600">
        {open.length > 0
          ? "Logo colors — confirm the upcharge with the client before invoicing"
          : "Logo colors — confirmed with the client"}
      </p>
      <p className="mt-1 text-xs text-brown-500">
        The website charged the 1-color price. Each extra color is $50 setup + $0.40 a piece. Email the client, then record their answer.
      </p>
      <ul className="mt-3 space-y-3">
        {checks.map((c) => {
          const total = c.suggested.reduce((s, l) => s + Number(l.unitPrice) * l.quantity, 0);
          return (
            <li key={c.id} className="rounded-md border border-rule bg-white px-3 py-2">
              <p className="text-sm text-ink">{c.message}</p>
              {c.suggested.length > 0 && (
                <ul className="mt-1 text-xs text-brown-500">
                  {c.suggested.map((l, i) => (
                    <li key={i}>
                      Suggested (not on the order yet): {l.description} — {l.quantity} × {money(Number(l.unitPrice))} = {money(Number(l.unitPrice) * l.quantity)}
                    </li>
                  ))}
                  <li className="font-medium">Upcharge if the client agrees: {money(total)}</li>
                </ul>
              )}
              {c.status === "pending" ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button type="button" size="sm" disabled={resolve.isPending}
                    onClick={() => resolve.mutate({ checkId: c.id, decision: "client_confirmed" })}
                    title={c.suggested.length ? "Adds the suggested charges to this order." : "Records that the client agreed; add the charge to the order yourself."}>
                    {c.suggested.length ? "Client confirmed — add the charges" : "Client confirmed"}
                  </Button>
                  <Button type="button" size="sm" variant="outline" disabled={resolve.isPending}
                    onClick={() => resolve.mutate({ checkId: c.id, decision: "client_declined" })}>
                    Client declined (1 color)
                  </Button>
                </div>
              ) : (
                <p className="mt-1 text-xs text-brown-500">
                  {c.status === "client_confirmed" ? "Client confirmed — charges added to the order." : "Client declined — printing in 1 color."}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {error && <p className="mt-2 text-xs text-rust-600">{error}</p>}
    </div>
  );
}
